import { useState } from "react";
import { IMAGES } from "@/lib/constants";
import ScrollReveal from "@/components/ScrollReveal";
import { Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", inquiry_type: "Other", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success("Thank you! We will get back to you soon.");
        setFormData({ name: "", email: "", phone: "", inquiry_type: "Other", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Unable to submit. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.fields})` }} />
        <div className="absolute inset-0 bg-[#1B4332]/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-20">
          <ScrollReveal>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-4">Contact Us</h1>
            <p className="text-white/80 text-base sm:text-lg font-outfit max-w-xl mx-auto">
              We'd love to hear from you. Reach out to us for partnerships, volunteering, or any inquiries.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <ScrollReveal>
                <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Contact Details</p>
                <h2 className="font-playfair text-2xl sm:text-3xl text-[#1B4332] font-bold mb-8">Get in Touch</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1B4332] flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-[#D4A843]" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-semibold text-[#1B4332] mb-1">Registered Office</h4>
                      <p className="text-[#666] font-outfit text-sm leading-relaxed">
                        Jharkhand, India<br />
                        Primary Operations: Khunti, Ranchi, Lohardaga, Hazaribagh
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1B4332] flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-[#D4A843]" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-semibold text-[#1B4332] mb-1">Email</h4>
                      <a href="mailto:contact@mbtwelfaretrust.org" className="text-[#2D6A4F] font-outfit text-sm hover:text-[#D4A843] transition-colors">
                        contact@mbtwelfaretrust.org
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1B4332] flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-[#D4A843]" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-semibold text-[#1B4332] mb-1">Phone</h4>
                      <a href="tel:+919939281943" className="text-[#2D6A4F] font-outfit text-sm hover:text-[#D4A843] transition-colors">
                        +91 99392 81943
                      </a>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-8 rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    title="MBT Trust Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d117487.66774755064!2d85.2371!3d23.0401!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f5e31989f0e2b5%3A0xeeec8a16c523486d!2sKhunti%2C%20Jharkhand!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="google-map"
                  />
                </div>
              </ScrollReveal>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <ScrollReveal delay={0.2}>
                <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 md:p-8 shadow-[0_4px_20px_rgba(27,67,50,0.08)]">
                  <h3 className="font-playfair text-xl text-[#1B4332] font-semibold mb-6">Send us a Message</h3>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Name *</label>
                      <input
                        type="text"
                        name="name"
                        data-testid="contact-form-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Email *</label>
                        <input
                          type="email"
                          name="email"
                          data-testid="contact-form-email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          data-testid="contact-form-phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Type of Inquiry</label>
                      <select
                        data-testid="contact-form-type"
                        value={formData.inquiry_type}
                        onChange={(e) => setFormData({ ...formData, inquiry_type: e.target.value })}
                        className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                      >
                        <option value="Volunteer">Volunteer</option>
                        <option value="Partner">Partner / CSR</option>
                        <option value="Donate">Donate</option>
                        <option value="Media">Media</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Message *</label>
                      <textarea
                        name="message"
                        data-testid="contact-form-message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={5}
                        className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="contact-form-submit"
                      disabled={submitting}
                      className="w-full bg-[#D4A843] text-[#1B4332] px-6 py-3.5 rounded-xl font-bold font-outfit text-base hover:bg-[#D4A843]/90 hover:shadow-lg transition-all disabled:opacity-60"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </form>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
